/* eslint-disable no-mixed-operators */
import { Injectable } from '@angular/core';

import {
  BehaviorSubject, Observable, combineLatest,
  of
} from 'rxjs';
import {
  filter,
  map, share, shareReplay, take, tap
} from 'rxjs/operators';

import { ClientReferralHttpService } from '@sondermind/data-access/client-referral';
import { CurrentUser, ClientUserTags } from '@sondermind/data-access/client-user';
import { IAPIListRequestOptions } from '@sondermind/utilities/models-http';
import { ITherapistTemplate } from '@sondermind/utilities/models-therapist';
import { ClientUser } from '@sondermind/utilities/models-user-client';

import { TherapistHttpService } from '../services/therapist.httpservice';

import { TherapistLogicService } from '../services/therapist-logic.service';
import { ITherapistStore } from './therapist-store.interface';

const EXPIRED_AND_TIMED_OUT_FILTER: Partial<IAPIListRequestOptions> = {
  filterArray: [
    { key: 'exclude_aasm_states', value: 'timed_out' },
    { key: 'exclude_aasm_states', value: 'expired' }
  ]
};

interface ITherapistStoreState {
  fullListNoExpired: ITherapistTemplate[];
  patOrRtsList: ITherapistTemplate[];
  acceptedAndRequestedList: ITherapistTemplate[];
  scheduledList: ITherapistTemplate[];
  notifiedList: ITherapistTemplate[];
  additionalList: ITherapistTemplate[];
  memberSelectedList: ITherapistTemplate[];
  declinedList: ITherapistTemplate[];
  onHoldList: ITherapistTemplate[];
  hasAnyMatches: boolean;
  hasActionableTherapist: boolean;
  inPostSessionRequestState: boolean;
}

@Injectable()
export class TherapistStore implements ITherapistStore {
  allTherapistsHttpRequest$?: Observable<ITherapistTemplate[]>;
  private therapistListSubj: BehaviorSubject<ITherapistTemplate[]> = new BehaviorSubject<ITherapistTemplate[]>([]);
  private expiredAndTimedOutTherapistListSubject$: BehaviorSubject<ITherapistTemplate[] | null>
    = new BehaviorSubject<ITherapistTemplate[] | null>(null);

  loadingComplete$ = new BehaviorSubject(false);

  // used to quickly determine display types in components
  allTherapistsDeclined: boolean = false;
  oneOfManyTherapistDeclined: boolean = false;
  allTherapistsDirectScheduleEnabled: boolean = false;
  hasManualMatches: boolean = false;
  emptyOrHasActionableProviders: boolean = false;
  hasScheduledTherapist: boolean = false;
  hasSingleActiveMatch: boolean = false;
  noMatchesYet: boolean = false;
  noTherapistsDirectScheduleEnabled: boolean = false;
  showMatchedTherapistLists: boolean = false;

  private therapistStoreState$: BehaviorSubject<ITherapistStoreState> = new BehaviorSubject<ITherapistStoreState>(
    {
      fullListNoExpired: [],
      patOrRtsList: [],
      acceptedAndRequestedList: [],
      scheduledList: [],
      notifiedList: [],
      additionalList: [],
      memberSelectedList: [],
      declinedList: [],
      onHoldList: [],
      hasAnyMatches: false,
      hasActionableTherapist: false,
      inPostSessionRequestState: false
    }
  );

  hasAnyMatches$: Observable<boolean> = of(false);
  hasActionableTherapist$: Observable<boolean> = of(false);
  inPostSessionRequestState$: Observable<boolean> = of(false);

  constructor(
    private currentUser: CurrentUser,
    private therapistHttpService: TherapistHttpService,
    private referralDataService: ClientReferralHttpService,
    private therapistLogicSvc: TherapistLogicService,
  ) {}

  fullTherapistList$(): Observable<ITherapistTemplate[]> {
    if (this.loadingComplete$.value || this.therapistListSubj.value.length > 0) {
      return this.therapistListSubj.asObservable();
    }
    return this.fetchTherapistsAndBuildStates();
  }

  private fetchTherapistsAndBuildStates(): Observable<ITherapistTemplate[]> {
    this.loadingComplete$.next(false);

    return this.getAllTherapistsHttpRequest$().pipe(
      tap((resp: ITherapistTemplate[]) => {
        this.updateStoreBooleanFlags(resp);
        this.therapistStoreState$.next(this.buildTherapistLists(resp));
        this.therapistListSubj.next(this.therapistStoreState$.value.fullListNoExpired);

        this.loadingComplete$.next(true);
      }),
      map(() => this.therapistListSubj.value)
    );
  }

  private buildTherapistLists(fullList: ITherapistTemplate[]): ITherapistStoreState {
    // Pre-compute all derived lists in a single pass to reduce computations
    const fullListNoExpired: ITherapistTemplate[] = [];
    const patOrRtsList: ITherapistTemplate[] = [];
    const acceptedAndRequestedList: ITherapistTemplate[] = [];
    const scheduledList: ITherapistTemplate[] = [];
    const notifiedList: ITherapistTemplate[] = [];
    const additionalList: ITherapistTemplate[] = [];
    const memberSelectedList: ITherapistTemplate[] = [];
    const declinedList: ITherapistTemplate[] = [];
    const onHoldList: ITherapistTemplate[] = [];

    let hasAnyMatches = false;
    let hasActionableTherapist = false;
    let inPostSessionRequestState = false;

    // Populate all derived arrays, and some booleans
    fullList.forEach((therapist) => {
      if (!this.therapistLogicSvc.isFullyExpired(therapist)) {
        fullListNoExpired.push(therapist);

        if (this.therapistLogicSvc.isPendingPatRtsOrConsult(therapist)) {
          patOrRtsList.push(therapist);
        }
        if (this.therapistLogicSvc.isAcceptedAndRequestedTherapist(therapist)) {
          acceptedAndRequestedList.push(therapist);
        }
        if (this.therapistLogicSvc.isScheduled(therapist)) {
          scheduledList.push(therapist);
        }
        if (this.therapistLogicSvc.isActionable(therapist) && !this.therapistLogicSvc.isAdditionalMatch(therapist)) {
          notifiedList.push(therapist);
        }
        if (this.therapistLogicSvc.isAdditionalMatch(therapist)) {
          additionalList.push(therapist);
        }
        if (this.therapistLogicSvc.isMemberSelectedMatch(therapist)) {
          memberSelectedList.push(therapist);
        }

        if (this.therapistLogicSvc.isDeclined(therapist)) {
          declinedList.push(therapist);
        } else {
          hasAnyMatches = true;
        }

        if (this.therapistLogicSvc.isOnHoldMatch(therapist)) {
          onHoldList.push(therapist);
        }
        if (this.therapistLogicSvc.isActionable(therapist)) {
          hasActionableTherapist = true;
        }
        if (this.therapistLogicSvc.isTherapistPostSessionRequestState(therapist)) {
          inPostSessionRequestState = true;
        }
      }
    });

    return {
      fullListNoExpired,
      patOrRtsList,
      acceptedAndRequestedList,
      scheduledList,
      notifiedList,
      additionalList,
      memberSelectedList,
      declinedList,
      onHoldList,
      hasAnyMatches,
      hasActionableTherapist,
      inPostSessionRequestState
    };
  }

  /**
   * Propose a time or request to schedule therapist list
   */
  patOrRtsTherapistList$(): Observable<ITherapistTemplate[]> {
    return this.therapistStoreState$.pipe(map((state) => state.patOrRtsList));
  }

  /**
   * Therapists that have been accepted
   */
  acceptedAndRequestedTherapistList$(): Observable<ITherapistTemplate[]> {
    return this.therapistStoreState$.pipe(map((state) => state.acceptedAndRequestedList));
  }

  /**
   * Filter list of associated therapists by SCHEDULED status.
   */
  scheduledTherapistList$(): Observable<ITherapistTemplate[]> {
    return this.therapistStoreState$.pipe(map((state) => state.scheduledList));
  }

  /**
   * Filter list of associated therapists by NOTIFIED and ACCEPTED status.
   */
  notifiedTherapistList$(): Observable<ITherapistTemplate[]> {
    return this.therapistStoreState$.pipe(map((store) => store.notifiedList));
  }

  /**
   * Filter list of associated therapists by the additional match status
   */
  additionalTherapistList$(): Observable<ITherapistTemplate[]> {
    return this.therapistStoreState$.pipe(map((store) => store.additionalList));
  }

  /**
   * Filter list of associated therapists by the member selected status
   */
  memberSelectedTherapistList$(): Observable<ITherapistTemplate[]> {
    return this.therapistStoreState$.pipe(map((store) => store.memberSelectedList));
  }
  /**
   * Filter list of associated therapists by DECLINED status.
   */
  declinedTherapistList$(): Observable<ITherapistTemplate[]> {
    return this.therapistStoreState$.pipe(map((store) => store.declinedList));
  }

  getOnHoldTherapistList$(): Observable<ITherapistTemplate[]> {
    return this.therapistStoreState$.pipe(map((store) => store.onHoldList));
  }

  getTherapistByPersonaId$(personaProviderId: number): Observable<ITherapistTemplate> {
    return this.therapistStoreState$.pipe(
      map((store) => store.fullListNoExpired.find((therapist) => therapist.personaProviderId === personaProviderId))
    );
  }

  // provider invited clients do not have a client referral so it causes incorrect behavior when we apply
  // the exclude filter
  getTherapistFilter(): Partial<IAPIListRequestOptions> {
    if (this.currentUser?.user?.providerInvited) return {};
    return EXPIRED_AND_TIMED_OUT_FILTER;
  }

  getExpiredAndTimedOutTherapists$(): Observable<ITherapistTemplate[]> {
    if (this.expiredAndTimedOutTherapistListSubject$.value) {
      return this.expiredAndTimedOutTherapistListSubject$.asObservable();
    }

    const filters: Partial<IAPIListRequestOptions> = {
      filterArray: [
        { key: 'aasm_states', value: 'timed_out' },
        { key: 'aasm_states', value: 'expired' }
      ]
    };

    return this.therapistHttpService.listAll(filters).pipe(
      tap((therapists) => { this.expiredAndTimedOutTherapistListSubject$.next(therapists) }),
      share()
    );
  }

  waitUntilLoaded$(): Observable<void> {
    return this.loadingComplete$.pipe(
      filter((loaded) => loaded),
      take(1),
      map(() => undefined)
    );
  }

  /**
   * Load all therapists as well as the boolean variables associated with them
   * Create a observable pattern in order to subscribe to the result on other components
   * @returns voided observable
   */
  refreshTherapistStore$(): Observable<void> {
    return this.fetchTherapistsAndBuildStates().pipe(map(() => {}));
  }

  private getAllTherapistsHttpRequest$(): Observable<ITherapistTemplate[]> {
    this.allTherapistsHttpRequest$ ??= this.therapistHttpService
      .listAll(this.getTherapistFilter())
      .pipe(share());

    return this.allTherapistsHttpRequest$;
  }

  updateClientSentNcc$(personaProviderId: number): Observable<void> {
    return this.therapistHttpService.clientSentNcc(personaProviderId);
  }

  /**
   * Order is important here to set various state checks.
   */
  private updateStoreBooleanFlags(resp: ITherapistTemplate[]): void {
    const filteredList = resp.filter((t) => !this.therapistLogicSvc.isFullyExpired(t));

    this.hasScheduledTherapist = this.therapistLogicSvc.checkScheduledStatus(filteredList);
    this.emptyOrHasActionableProviders = this.therapistLogicSvc.checkEmptyOrActionable(filteredList);
    this.hasManualMatches = this.therapistLogicSvc.checkManualMatchedStatus(filteredList);
    this.noMatchesYet = this.therapistLogicSvc.checkNoMatchesYet(filteredList);
    this.allTherapistsDeclined = this.therapistLogicSvc.checkDeclinedStatus(filteredList);
    this.oneOfManyTherapistDeclined = this.therapistLogicSvc.checkOneOfManyDeclinedStatus(filteredList);
    this.allTherapistsDirectScheduleEnabled = this.therapistLogicSvc.checkAllDirectScheduleEnabled(filteredList);
    this.noTherapistsDirectScheduleEnabled = this.therapistLogicSvc.checkNoDirectScheduleEnabled(filteredList);
    this.hasSingleActiveMatch = this.therapistLogicSvc.getNumberOfActiveMatches(filteredList) === 1;
    this.showMatchedTherapistLists = !this.allTherapistsDeclined && !this.noMatchesYet;
    this.therapistListSubj.next(filteredList);
    this.checkIsPslClient();
  }

  /**
   * We want to show the PSL flow if they have a PSL tag or they have been declined by their one PSL Provider.
   * If all these conditions are not met, throw them into the normal flow.
   */
  checkIsPslClient(): void {
    this.currentUser.user$.pipe(take(1)).subscribe((user: ClientUser) => {
      const isPslClient = user?.tags?.includes(ClientUserTags.PSL_SOURCED) && !this.oneOfManyTherapistDeclined;
      this.currentUser.setIsPslClient(isPslClient);
    });
  }

  getNonDeclinedTherapistsOnMostRecentNcr$(): Observable<ITherapistTemplate[]> {
    return combineLatest({
      allTherapists: of(this.therapistStoreState$.value.fullListNoExpired),
      mostRecentReferral: this.referralDataService.mostRecentReferral$
    }).pipe(
      map((data) => data.allTherapists
        .filter((t) => !this.therapistLogicSvc.isDeclined(t))
        .filter((t) => !!this.therapistLogicSvc.getMatch(t, data.mostRecentReferral?.id))
      ),
      shareReplay(1)
    );
  }
}
